import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import api from '../services/api';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const { i18n, t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [isLoading, setIsLoading] = useState(false);

  // Set document direction based on language
  useEffect(() => {
    document.documentElement.dir = currentLanguage === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Fetch user's default language from settings when user logs in
  useEffect(() => {
    const auth = getAuth();
    let unsubscribeUser = null;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        // First, fetch the initial settings
        fetchUserLanguage(user);
        
        // Then, set up a listener for changes to the user's settings
        const userDocRef = doc(db, 'users', user.uid);
        unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            if (userData.defaultLanguage && userData.defaultLanguage !== currentLanguage) {
              changeLanguage(userData.defaultLanguage);
            }
          }
        }, (error) => {
          console.error('Error listening to user document:', error);
        });
      } else if (unsubscribeUser) {
        // Clean up subscriptions when user logs out
        unsubscribeUser();
        unsubscribeUser = null;
      }
    });
    
    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
    };
  }, []);

  const fetchUserLanguage = async (user) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const response = await api.get(`/user/${user.uid}/settings`);
      
      if (response.data && response.data.status === 'success') {
        const userData = response.data.data;
        if (userData.defaultLanguage && userData.defaultLanguage !== currentLanguage) {
          changeLanguage(userData.defaultLanguage);
        }
      }
    } catch (error) {
      console.error('Error fetching user language preference:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const changeLanguage = (language) => {
    if (language) {
      console.log(`Changing language to: ${language}`);
      i18n.changeLanguage(language);
      setCurrentLanguage(language);
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
